// Mobile Season Preview — shown right before the regular season starts (the regular-season
// phase change redirects here). Instead of ZenGM's league-wide SeasonPreview, this shows how
// the USER's own players developed over the offseason: OVR/POT and the change vs last season.
// Data comes from the getRatingsChanges worker action.

import { useEffect, useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { toWorker } from "../util/toWorker.ts";

const POS_COLORS: Record<string, string> = {
	PG: "#4A9EFF",
	SG: "#2f9fe0",
	SF: "#18E0A8",
	PF: "#E8B923",
	C: "#E2504E",
	GF: "#7C9FF0",
	FC: "#E8A020",
	F: "#9a6cff",
	G: "#4A9EFF",
};
const posColor = (pos: string) => POS_COLORS[pos] ?? "#4A9EFF";

type Row = {
	pid: number;
	name: string;
	pos: string;
	age: number;
	ovr: number;
	pot: number;
	dOvr: number | null;
	dPot: number | null;
	isNew: boolean;
};

// A signed delta chip: green for gains, red for drops, muted for no change.
const Delta = ({ d }: { d: number | null }) => {
	if (d === null) return <span className="gsp-delta gsp-new">NEW</span>;
	if (d === 0) return <span className="gsp-delta gsp-flat">—</span>;
	const up = d > 0;
	return (
		<span className={`gsp-delta ${up ? "gsp-up" : "gsp-down"}`}>
			{up ? "▲" : "▼"} {Math.abs(d)}
		</span>
	);
};

const GmSeasonPreview = () => {
	useTitleBar({ title: "Season Preview" });
	const [data, setData] = useState<{ season: number; rows: Row[] } | null>(null);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			const res = await toWorker("main", "getRatingsChanges", undefined);
			if (!cancelled) setData(res as any);
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const rows = data?.rows ?? [];
	const risers = rows.filter((r) => (r.dOvr ?? 0) > 0).length;

	return (
		<div className="gsp">
			<style>{styles}</style>

			<div className="gsp-hero">
				<div className="gsp-eyebrow">
					{data ? `${data.season} SEASON` : "SEASON"}
				</div>
				<div className="gsp-title">Offseason report</div>
				<div className="gsp-sub">
					How your roster developed{risers > 0 ? ` · ${risers} improved` : ""}
				</div>
			</div>

			<div className="gsp-headrow">
				<div className="gsp-h-player">PLAYER</div>
				<div className="gsp-h-stat">OVR</div>
				<div className="gsp-h-stat">POT</div>
			</div>

			{data && rows.length === 0 ? (
				<div className="gsp-empty">No players on your roster.</div>
			) : null}

			{rows.map((r) => (
				<div key={r.pid} className="gsp-row">
					<div className="gsp-dot" style={{ background: posColor(r.pos) }} />
					<div className="gsp-info">
						<div className="gsp-name">{r.name}</div>
						<div className="gsp-meta">
							{r.pos} · Age {r.age}
						</div>
					</div>
					<div className="gsp-stat">
						<div className="gsp-val">{r.ovr}</div>
						<Delta d={r.dOvr} />
					</div>
					<div className="gsp-stat">
						<div className="gsp-val">{r.pot}</div>
						<Delta d={r.dPot} />
					</div>
				</div>
			))}

			{!data ? <div className="gsp-empty">Loading…</div> : null}
		</div>
	);
};

const styles = `
.gsp { max-width:520px; margin:0 auto; padding:16px 16px 140px; color:#fff;
  font-family:Montserrat,system-ui,sans-serif; }
.gsp-hero { border-radius:16px; padding:18px; margin-bottom:18px;
  background:linear-gradient(135deg,#123a5c,#0f2740); border:1px solid #1f5c7a; }
.gsp-eyebrow { font-size:10.5px; font-weight:900; letter-spacing:1.4px; color:#7fc4e8; }
.gsp-title { font-size:26px; font-weight:900; letter-spacing:-.5px; margin-top:3px; }
.gsp-sub { font-size:12px; font-weight:700; color:#8fb3c9; margin-top:4px; }

.gsp-headrow { display:flex; align-items:center; padding:0 2px 8px; border-bottom:1px solid #2a2a2a; }
.gsp-h-player { flex:1; font-size:8.5px; font-weight:800; color:#6f7b83; letter-spacing:.5px; }
.gsp-h-stat { width:66px; text-align:center; font-size:8.5px; font-weight:800; color:#6f7b83; letter-spacing:.5px; }

.gsp-row { display:flex; align-items:center; gap:11px; padding:11px 2px; border-bottom:1px solid #1c1c1c; }
.gsp-dot { width:10px; height:10px; border-radius:50%; flex:none; }
.gsp-info { flex:1; min-width:0; }
.gsp-name { font-size:14px; font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.gsp-meta { font-size:10.5px; color:#8a8a8a; font-weight:700; margin-top:1px; }
.gsp-stat { width:66px; flex:none; display:flex; flex-direction:column; align-items:center; gap:2px; }
.gsp-val { font-size:16px; font-weight:900; }
.gsp-delta { font-size:10px; font-weight:800; }
.gsp-up { color:#18E0A8; }
.gsp-down { color:#E2504E; }
.gsp-flat { color:#55606a; }
.gsp-new { color:#7fc4e8; }
.gsp-empty { text-align:center; color:#8a8a8a; padding:30px 16px; font-size:12.5px; font-weight:700; }
`;

export default GmSeasonPreview;
